import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface WorkingHoursConfig {
  type: "default" | "recurring";
  date?: string;
  recurringenabled?: boolean;
  recurrenceType?: "day" | "weekly" | "biweekly" | "monthly";
  interval?: number;
  selectedDays?: ("monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday")[];
  monthlyPattern?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
  child?: Array<{
    date: string;
    startTime: string;
    endTime: string;
    totalHours: number;
  }>;
}

export interface OvertimeConfig {
  allowed: boolean;
  maxHoursPerDay?: number;
  maxHoursPerMonth?: number;
  overtimeRate?: number;
  requiresApproval: boolean;
}

export interface LateArrivalConfig {
  graceMinutes: number;
  markAsHalfDayAfter?: number;
  deductionEnabled?: boolean;
  deductMinutesFor?: number;
  forEveryMinutes?: number;
}

export interface EarlyDepartureConfig {
  graceMinutes: number;
  markHalfDayBeforeMinutes?: number;
}

export interface DepartmentBreak {
  type: "duration" | "timeRange";
  durationHours?: number;
  hours?: number;
  startTime?: string;
  endTime?: string;
}

export interface DepartmentLocation {
  lat: number | string;
  lng: number | string;
}

export interface Department {
  _id: string;
  name: string;
  description?: string;
  handbook?: string;
  status: "active" | "inactive";
  organization: string;
  members: string[];
  workingHours?: WorkingHoursConfig[];
  overtimeConfig?: OvertimeConfig;
  lateArrivalConfig?: LateArrivalConfig;
  earlyDepartureConfig?: EarlyDepartureConfig;
  break?: DepartmentBreak;
  location?: DepartmentLocation;
  isLocationCheck?: boolean;
  director?: string;
  logo?: string;
  timezone?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type DepartmentsPayload = {
  name: string;
  description?: string;
  handbook?: string;
  status: "active" | "inactive";
  organization: string;
  members?: string[];
  workingHours?: WorkingHoursConfig[];
  overtimeConfig?: OvertimeConfig;
  lateArrivalConfig?: LateArrivalConfig;
  earlyDepartureConfig?: EarlyDepartureConfig;
  break?: DepartmentBreak;
  location?: DepartmentLocation;
  isLocationCheck?: boolean;
  director?: string;
  departmentLogo?: string | null;
  timezone?: string;
};

export type DepartmentsResponse = {
  success: boolean;
  message: string;
  data: Department;
};

const addDepartment = async (
  payload: DepartmentsPayload
): Promise<DepartmentsResponse> => {
  const { data } = await customAxios.post<DepartmentsResponse>(
    "/departments",
    payload
  );
  return data;
};

export const useAddDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation<DepartmentsResponse, AxiosError, DepartmentsPayload>({
    mutationFn: addDepartment,
    onSuccess: async (data) => {
      showNotification({
        title: "Success",
        message: data.message,
        color: "green",
      });

      await queryClient.invalidateQueries(["departments"]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to add department",
        color: "red",
      }),
  });
};
